import { apiClient } from '@/core/api/client';

export interface WorkloadResponsable {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: 'admin' | 'bureau';
  days_per_week: number;
  current_active_affaire_count: number;
  workload_ratio: number;
  capacity_percentage: number;
}

export interface WorkloadSummary {
  total_responsables: number;
  total_active_affaires: number;
  total_capacity_days: number;
  average_ratio: number;
  min_ratio: number;
  max_ratio: number;
}

export interface WorkloadStatistics {
  responsables: WorkloadResponsable[];
  summary: WorkloadSummary;
}

export interface UnassignedAffaire {
  affaire_id: number;
  status: string;
  encodeur: string | null;
  last_activity_date: string | null;
  last_activity_type: string | null;
  numero_rue: string | null;
  adresse_rue_bien: string | null;
  commune_bien: string | null;
}

export interface AssignmentStats {
  total: number;
  assigned: number;
  failed: number;
  skipped: number;
  errors: Array<{
    affaireId: number;
    error: string;
  }>;
}

/**
 * Get workload statistics for all responsables
 * Note: apiClient interceptor unwraps response.data automatically
 */
export async function getWorkloadStatistics(): Promise<WorkloadStatistics> {
  try {
    // apiClient returns { success, data } directly (interceptor unwraps axios response)
    const response = await apiClient.get<{ success: boolean; data: WorkloadStatistics }>(
      '/api/tracker/affaires/workload-stats'
    );

    // response.data is WorkloadStatistics (single unwrap, not double)
    if (response.data) {
      return response.data;
    }

    // Fallback if response has responsables directly (legacy format)
    if ((response as unknown as WorkloadStatistics).responsables) {
      return response as unknown as WorkloadStatistics;
    }

    // Fallback to empty data if structure is unexpected
    console.warn('⚠️ Unexpected response structure, returning empty data');
    return {
      responsables: [],
      summary: {
        total_responsables: 0,
        total_active_affaires: 0,
        total_capacity_days: 0,
        average_ratio: 0,
        min_ratio: 0,
        max_ratio: 0
      }
    };
  } catch (error) {
    console.error('❌ Error fetching workload statistics:', error);
    // Return empty data instead of throwing to prevent React Query errors
    return {
      responsables: [],
      summary: {
        total_responsables: 0,
        total_active_affaires: 0,
        total_capacity_days: 0,
        average_ratio: 0,
        min_ratio: 0,
        max_ratio: 0
      }
    };
  }
}

/**
 * Get list of unassigned affaires
 * Note: apiClient interceptor unwraps response.data automatically
 */
export async function getUnassignedAffaires(): Promise<UnassignedAffaire[]> {
  const response = await apiClient.get<{ success: boolean; data: UnassignedAffaire[] }>(
    '/api/tracker/affaires/unassigned'
  );
  return response.data;
}

/**
 * Manually trigger auto-assignment for a specific affaire
 * Note: apiClient interceptor unwraps response.data automatically
 */
export async function autoAssignAffaire(affaireId: number): Promise<{
  affaireId: number;
  responsable: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  changeReason: string;
}> {
  const response = await apiClient.post<{ success: boolean; data: {
    affaireId: number;
    responsable: {
      id: string;
      first_name: string;
      last_name: string;
      email: string;
    };
    changeReason: string;
  } }>(
    `/api/tracker/affaires/${affaireId}/auto-assign`
  );
  return response.data;
}

/**
 * Process all unassigned affaires (bulk auto-assignment)
 * Note: apiClient interceptor unwraps response.data automatically
 */
export async function processUnassignedAffaires(): Promise<AssignmentStats> {
  const response = await apiClient.post<{ success: boolean; data: AssignmentStats }>(
    '/api/tracker/affaires/process-unassigned'
  );
  return response.data;
}

/**
 * Redistribute all affaires from a specific user to others
 * Used when removing someone from responsables
 * Note: apiClient interceptor unwraps response.data automatically
 */
export async function redistributeFromUser(params: {
  userId: string;
  changedByUserId?: string | null;
  notes?: string | null;
}): Promise<AssignmentStats> {
  const response = await apiClient.post<{ success: boolean; data: AssignmentStats }>(
    '/api/tracker/affaires/redistribute-from-user',
    params
  );
  return response.data;
}

/**
 * Rebalance ALL active affaires based on current capacities
 * Used when updating someone's capacity
 */
export async function rebalanceAllAffaires(params: {
  changedByUserId?: string | null;
  notes?: string | null;
}): Promise<{
  total: number;
  reassigned: number;
  unchanged: number;
  failed: number;
  errors: Array<{ affaireId: number; error: string }>;
}> {
  const response = await apiClient.post<{ success: boolean; data: {
    total: number;
    reassigned: number;
    unchanged: number;
    failed: number;
    errors: Array<{ affaireId: number; error: string }>;
  } }>(
    '/api/tracker/affaires/rebalance-all',
    params
  );
  return response.data;
}
